import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Pagination,
  InputAdornment,
  Switch
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Security,
  Search,
  CheckCircle,
  Cancel,
  AdminPanelSettings,
  PersonAdd,
  Lock
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import superAdminService from '../../services/superAdminService';

interface SuperAdmin {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isActive: boolean;
  isPrimarySuperAdmin?: boolean;
  permissions?: Record<string, boolean>;
  createdAt: string;
}

interface PermissionItem {
  key: string;
  label: string;
  category: string;
}

const SuperAdminManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [superAdmins, setSuperAdmins] = useState<SuperAdmin[]>([]);
  const [permissionsList, setPermissionsList] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPrimary, setIsPrimary] = useState<boolean | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<SuperAdmin | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    permissions: {} as Record<string, boolean>
  });

  // Check primary status from backend (not cached AuthContext)
  useEffect(() => {
    const checkPrimaryStatus = async () => {
      try {
        const response = await superAdminService.checkPrimarySuperAdmin() as any;
        // Service extracts the data property, so response contains isPrimarySuperAdmin directly
        setIsPrimary(response.isPrimarySuperAdmin);
      } catch (err) {
        setIsPrimary(false);
      }
    };
    checkPrimaryStatus();
  }, []);

  useEffect(() => {
    if (isPrimary) {
      fetchSuperAdmins();
      fetchPermissionsList();
    }
  }, [page, searchQuery, isPrimary]);

  const fetchSuperAdmins = async () => {
    try {
      setLoading(true);
      const data = await superAdminService.getSuperAdmins({
        page,
        limit: 10,
        search: searchQuery
      }) as any;
      // Service returns data directly (extracted from response)
      if (data && data.superAdmins) {
        setSuperAdmins(data.superAdmins);
        setTotalPages(data.totalPages);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch super admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionsList = async () => {
    try {
      const data = await superAdminService.getPermissionsList() as any;
      // Service returns data directly (extracted from response)
      if (data && Array.isArray(data)) {
        setPermissionsList(data);
        // Initialize default permissions - all disabled by default (selective access)
        const defaultPermissions: Record<string, boolean> = {};
        data.forEach((p: PermissionItem) => {
          defaultPermissions[p.key] = false;
        });
        // Only enable blog posts by default as an example
        defaultPermissions.blogPosts = true;
        setFormData(prev => ({ ...prev, permissions: defaultPermissions }));
      }
    } catch (err) {
      console.error('Failed to fetch permissions list:', err);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await superAdminService.createSuperAdmin({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        permissions: formData.permissions
      });
      if (response.success) {
        setCreateDialogOpen(false);
        fetchSuperAdmins();
        resetForm();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create super admin');
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedAdmin) return;
    try {
      const response = await superAdminService.updateSuperAdminPermissions(
        selectedAdmin._id,
        selectedAdmin.permissions || {}
      );
      if (response.success) {
        setEditDialogOpen(false);
        fetchSuperAdmins();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update permissions');
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;
    try {
      const response = await superAdminService.deleteSuperAdmin(selectedAdmin._id);
      if (response.success) {
        setDeleteDialogOpen(false);
        fetchSuperAdmins();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete super admin');
    }
  };

  const resetForm = () => {
    const defaultPermissions: Record<string, boolean> = {};
    permissionsList.forEach((p: PermissionItem) => {
      defaultPermissions[p.key] = true;
    });
    defaultPermissions.systemSettings = false;
    defaultPermissions.superAdminManagement = false;
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      permissions: defaultPermissions
    });
  };

  const openEditDialog = (admin: SuperAdmin) => {
    setSelectedAdmin(admin);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (admin: SuperAdmin) => {
    setSelectedAdmin(admin);
    setDeleteDialogOpen(true);
  };

  const togglePermission = (key: string) => {
    if (selectedAdmin) {
      setSelectedAdmin({
        ...selectedAdmin,
        permissions: {
          ...selectedAdmin.permissions,
          [key]: !(selectedAdmin.permissions?.[key] ?? true)
        }
      });
    } else {
      setFormData({
        ...formData,
        permissions: {
          ...formData.permissions,
          [key]: !formData.permissions[key]
        }
      });
    }
  };

  const groupedPermissions = permissionsList.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, PermissionItem[]>);

  if (isPrimary === null) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isPrimary) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Access Denied</AlertTitle>
          Only the primary super admin can manage other super admins.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security color="primary" />
          Super Admin Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage super admin accounts and their access permissions. Only the primary super admin can access this page.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current User Info */}
      <Card sx={{ mb: 4, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AdminPanelSettings sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6">
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Primary Super Admin
              </Typography>
            </Box>
            <Chip
              label="Primary"
              color="secondary"
              size="small"
              sx={{ ml: 'auto' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Actions Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <TextField
          placeholder="Search super admins..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Add Super Admin
        </Button>
      </Box>

      {/* Super Admins Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : superAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No super admins found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              superAdmins.map((admin) => (
                <TableRow key={admin._id}>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {admin.firstName} {admin.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={admin.isActive ? <CheckCircle /> : <Cancel />}
                      label={admin.isActive ? 'Active' : 'Inactive'}
                      color={admin.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {admin.isPrimarySuperAdmin ? (
                      <Chip
                        label="Primary"
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="Secondary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(admin.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {!admin.isPrimarySuperAdmin && (
                      <>
                        <Tooltip title="Edit Permissions">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(admin)}
                            sx={{ mr: 1 }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeleteDialog(admin)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {admin.isPrimarySuperAdmin && (
                      <Tooltip title="Cannot modify primary super admin">
                        <Lock fontSize="small" color="disabled" />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAdd color="primary" />
            Add New Super Admin
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                required
                helperText="Minimum 8 characters"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Permissions
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            All permissions are disabled by default. Toggle ON the features this super admin should be able to access.
          </Alert>

          {Object.entries(groupedPermissions).map(([category, items]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                {category}
              </Typography>
              <Grid container spacing={2}>
                {items.map((permission) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={permission.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.permissions[permission.key] ?? true}
                          onChange={() => togglePermission(permission.key)}
                        />
                      }
                      label={permission.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!formData.firstName || !formData.lastName || !formData.email || !formData.password || formData.password.length < 8}
          >
            Create Super Admin
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit color="primary" />
            Edit Permissions: {selectedAdmin?.firstName} {selectedAdmin?.lastName}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Be careful when granting System Settings and Super Admin Management permissions.
          </Alert>

          {Object.entries(
            permissionsList.reduce((acc, item) => {
              if (!acc[item.category]) {
                acc[item.category] = [];
              }
              acc[item.category].push(item);
              return acc;
            }, {} as Record<string, PermissionItem[]>)
          ).map(([category, items]) => (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                {category}
              </Typography>
              <Grid container spacing={2}>
                {items.map((permission) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={permission.key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedAdmin?.permissions?.[permission.key] ?? true}
                          onChange={() => togglePermission(permission.key)}
                        />
                      }
                      label={permission.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdatePermissions} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Super Admin</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedAdmin?.firstName} {selectedAdmin?.lastName}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminManagementPage;
